import range from 'lodash/range';

import EntityPageType from '@/enums/EntityPageType';
import { getCookie } from '@/helpers/cookies';

export default {
  data() {
    return {
      hasScrollListener: false,
      postElement: null,
      postObserver: null,
      renderHeight: 1000,
      renderTimer: null,
      scrollPercentage: 0,
      shouldRender: true,
      slugsTracked: [],
      unrenderTimer: null,
    };
  },

  beforeUnmount() {
    if (this.hasScrollListener) {
      window.removeEventListener('scroll', this.handleScroll);
    }

    this.postObserver?.disconnect();
  },

  computed: {
    isPetHealth() {
      return [
        EntityPageType.Condition,
        EntityPageType.Symptom,
      ].includes(this.postType);
    },
    postSlug() {
      return this.post?.slug ?? this.startingSlug;
    },
    postUrl() {
      return `/en/${this.post?.post_type?.slug ?? this.postType}/${this.postSlug}`;
    },
  },

  methods: {
    buildPetHealthPostUrl() {
      const segments = window.location.pathname.split('/');

      segments.pop();
      segments.push(this.postSlug);

      return segments.join('/');
    },

    changeURL() {
      const page = this.isPetHealth ? this.buildPetHealthPostUrl() : this.postUrl;

      if (window.location.pathname === page) {
        return;
      }

      this.$emit('render', this.postSlug);
      const title = `${this.post?.title ?? this.postTitle} - Vetster`;
      window.history.pushState({ page, title }, title, page);
      document.title = title;

      this.trackNextPageLoad(this.post?.slug);
    },

    handlePostIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.isScrollingIntoView(entry)) {
            this.changeURL();
          }

          clearTimeout(this.unrenderTimer);
          this.renderTimer = setTimeout(() => {
            this.shouldRender = true;
          }, 50);
        } else {
          clearTimeout(this.renderTimer);
          this.unrenderTimer = setTimeout(() => {
            this.shouldRender = false;
          }, 150);
        }
      });
    },

    handleScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const {
        clientHeight,
        scrollHeight,
      } = document.documentElement;
      this.scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
    },

    isScrollingIntoView(entry) {
      const scrollPercentage = (this.renderHeight * entry.intersectionRatio) / window.innerHeight;

      return (this.postElement.getBoundingClientRect().top > 0 && scrollPercentage > 0.5) || scrollPercentage > 0.6;
    },

    observePost(element) {
      // Legacy Safari iOS browser.
      if (!window.IntersectionObserver) {
        return;
      }

      this.postElement = element;
      this.postObserver = new IntersectionObserver(this.handlePostIntersection, {
        threshold: range(0, 1, 0.01).map((n) => Number(n.toFixed(2))).concat([1]),
      });
      this.postObserver.observe(element);
      this.renderHeight = element.clientHeight;
    },

    observeScrollHeight() {
      window.addEventListener('scroll', this.handleScroll);
      this.hasScrollListener = true;
    },

    trackNextPageLoad(slug) {
      if (slug && !this.slugsTracked.includes(slug)) {
        this.slugsTracked.push(slug);
        this.$analytics.track({
          eventName: 'next_page_load',
          parent: 'global',
          child: 'webpage',
          uat_id: getCookie('v-uatid'),
        });
      }
    },
  },
};
